import { Link } from "react-router";
import { CheckSquare, Clock, ListChecks } from "lucide-react";
import {
  Body,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Subtitle,
  VButton,
  VKpiCard,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useTasksByAssignee, useUpdateTaskStatus, TaskCard } from "@/domains/tasks";
import type { CameraTask, TaskStatus } from "@/domains/tasks";

export function PartnerDashboard(): React.JSX.Element {
  const user = useCurrentUser();
  const { data } = useTasksByAssignee(user.id);
  const updateStatus = useUpdateTaskStatus();

  const list = data ?? [];
  const today = "2026-05-11";
  const open = list.filter((t) => t.status !== "done");
  const todayList = open.filter((t) => t.dueDate === today);
  const upcoming = open.filter((t) => t.dueDate > today);
  const overdue = open.filter((t) => t.dueDate < today);

  const handleToggle = (task: CameraTask): void => {
    const next: TaskStatus = task.status === "done" ? "todo" : "done";
    updateStatus.mutate({ id: task.id, status: next });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Hallo ${user.firstName}`}
        subtitle={`Taken toegewezen door Politiezone Antwerpen — ${user.organisation}.`}
        actions={
          <VButton variant="outline" asChild>
            <Link to="/tasks/list">
              <ListChecks className="size-4" aria-hidden />
              Alle taken
            </Link>
          </VButton>
        }
      />

      <div className="grid grid-cols-3 gap-4">
        <VKpiCard
          label="Te laat"
          value={overdue.length}
          helper="Vereist directe actie"
          icon={<Clock className="size-4 text-error-600" aria-hidden />}
        />
        <VKpiCard
          label="Vandaag"
          value={todayList.length}
          helper={`Datum: ${today}`}
          icon={<CheckSquare className="size-4" aria-hidden />}
        />
        <VKpiCard
          label="Komende dagen"
          value={upcoming.length}
          helper="Plan in"
          icon={<ListChecks className="size-4" aria-hidden />}
        />
      </div>

      {[
        { label: "Te laat", tasks: overdue },
        { label: "Vandaag", tasks: todayList },
        { label: "Komende dagen", tasks: upcoming },
      ].map((bucket) =>
        bucket.tasks.length === 0 ? null : (
          <Card key={bucket.label} padding="lg" className="space-y-3">
            <div className="flex items-center justify-between">
              <CardTitle>{bucket.label}</CardTitle>
              <Subtitle>{bucket.tasks.length} taak/taken</Subtitle>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              {bucket.tasks.map((task) => (
                <div key={task.id} className="flex items-start gap-2">
                  <VButton
                    variant="ghost"
                    size="icon"
                    aria-label="Afvinken"
                    onClick={() => handleToggle(task)}
                    icon={
                      <CheckSquare
                        className={
                          task.status === "done"
                            ? "size-5 text-success-600"
                            : "size-5 text-muted-foreground"
                        }
                        aria-hidden
                      />
                    }
                  />
                  <TaskCard task={task} className="flex-1" />
                </div>
              ))}
            </div>
          </Card>
        ),
      )}

      {open.length === 0 ? (
        <EmptyState
          title="Geen openstaande taken"
          description="Alle taken zijn afgevinkt. Bedankt!"
          action={
            <Link to="/tasks/list" className="text-sm font-medium text-primary-700 hover:underline">
              Bekijk archief
            </Link>
          }
        />
      ) : null}

      <Card padding="lg">
        <Body tone="muted">
          Heb je een vraag over een taak of dossier? Reageer rechtstreeks op de mail die je
          ontving, of contacteer Lien Vandevelde (team functionele ondersteuning).
        </Body>
      </Card>
    </div>
  );
}
